import { auth } from 'utils/firebaseConfig';
import axios from 'axios';
import { filterMusicEnginePodcasts, filterTrendingChannels, logFilteringStats } from 'utils/podcastFilter';

// MusicEngine API URL - configured via environment variable
// For production: https://listen.sheqonomi.com/api
// For local development: http://localhost/Sheqonomi-MusicEngine/public/api
const MUSIC_ENGINE_URL = process.env.REACT_APP_MUSIC_ENGINE_URL || 'https://listen.sheqonomi.com/api';

// Resolve base to match current scheme to avoid mixed-content blocking
const RESOLVED_ME_BASE = (() => {
    let base = MUSIC_ENGINE_URL || '';
    try {
        if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && base.startsWith('http://')) {
            base = base.replace(/^http:/, 'https:');
        }
    } catch (_) {}
    return base;
})();

// MusicEngine API instance
const musicEngineInstance = axios.create({
    baseURL: RESOLVED_ME_BASE,
});

let musicEngineAccessToken = localStorage.getItem('me_access_token');
let musicEngineApiBaseOverride = null; // e.g., https://listen.sheqonomi.com/api

// MusicEngine API instance interceptor
musicEngineInstance.interceptors.request.use(
    async (config) => {
        // Add MusicEngine specific headers
        config.headers['Accept'] = 'application/json';
        // Only set JSON content type when not sending FormData (let browser set multipart boundaries)
        if (!(config.data instanceof FormData)) {
            config.headers['Content-Type'] = 'application/json';
        }
        if (musicEngineAccessToken) {
            config.headers['Authorization'] = `Bearer ${musicEngineAccessToken}`;
        }
        // Add platform identifier for podcast blacklist filtering
        config.headers['X-Platform'] = 'kaios';
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// MusicEngine API functions
export const musicEngineGet = async (endpoint, params = {}) => {
    try {
        const base = (musicEngineApiBaseOverride || RESOLVED_ME_BASE).replace(/\/$/, '');
        const ep = typeof endpoint === 'string' && endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const url = `${base}${ep}`;
        const headers = { 
            Accept: 'application/json',
            'X-Platform': 'kaios' // Add platform identifier for podcast blacklist filtering
        };
        if (musicEngineAccessToken) {
            headers['Authorization'] = `Bearer ${musicEngineAccessToken}`;
        }
        const response = await axios.get(url, { params, headers });
        return response.data;
    } catch (error) {
        throw error.response?.data?.message || 'MusicEngine API error';
    }
};

export const musicEnginePost = async (endpoint, data = {}) => {
    try {
        const headers = { 
            Accept: 'application/json',
            'X-Platform': 'kaios' // Add platform identifier for podcast blacklist filtering
        };
        if (!(data instanceof FormData)) headers['Content-Type'] = 'application/json';
        if (musicEngineAccessToken) headers['Authorization'] = `Bearer ${musicEngineAccessToken}`;
        const baseURL = musicEngineApiBaseOverride || RESOLVED_ME_BASE;
        const base = (baseURL || '').replace(/\/$/, '');
        const ep = typeof endpoint === 'string' && endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const response = await axios.post(`${base}${ep}`, data, { headers });
        return response.data;
    } catch (error) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message || 'MusicEngine API error';
        throw { message, status };
    }
};

// Points: fetch balance for logged-in MusicEngine user
export const getMusicEnginePointsBalance = async () => {
    try {
        // Prefer instance GET to ensure baseURL and interceptors apply
        const resp = await musicEngineInstance.get('/auth/user/points/balance');
        const data = resp?.data || {};
        return {
            totalPoints: Number(data?.total_points || 0),
            totalEarned: Number(data?.total_earned || 0),
            totalSpent: Number(data?.total_spent || 0),
        };
    } catch (error) {
        // Fallback: construct absolute URL and use token directly from storage
        try {
            const base = (musicEngineApiBaseOverride || MUSIC_ENGINE_URL).replace(/\/$/, '');
            const url = `${base}/auth/user/points/balance`;
            const token = musicEngineAccessToken || localStorage.getItem('me_access_token');
            const headers = { Accept: 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            const resp = await axios.get(url, { headers });
            const data = resp?.data || {};
            return {
                totalPoints: Number(data?.total_points || 0),
                totalEarned: Number(data?.total_earned || 0),
                totalSpent: Number(data?.total_spent || 0),
            };
        } catch (fallbackErr) {
            throw fallbackErr;
        }
    }
};

export const getMusicEnginePointsHistory = async (page = 1, perPage = 20) => {
    try {
        const resp = await musicEngineInstance.get('/auth/user/points/history', { params: { page, per_page: perPage } });
        return resp?.data || { data: [], pagination: {} };
    } catch (err) {
        // Fallback to absolute
        const base = (musicEngineApiBaseOverride || RESOLVED_ME_BASE).replace(/\/$/, '');
        const url = `${base}/auth/user/points/history`;
        const token = musicEngineAccessToken || localStorage.getItem('me_access_token');
        const headers = { Accept: 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await axios.get(url, { params: { page, per_page: perPage }, headers });
        return resp?.data || { data: [], pagination: {} };
    }
};

// Raw fetch fallback to guarantee a visible network call in devtools
export const fetchMusicEnginePointsBalanceRaw = async () => {
    const base = (musicEngineApiBaseOverride || RESOLVED_ME_BASE || '').replace(/\/$/, '');
    const url = `${base}/auth/user/points/balance`;
    const token = musicEngineAccessToken || localStorage.getItem('me_access_token');
    const headers = { 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const resp = await fetch(url, { method: 'GET', headers });
    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Balance fetch failed: ${resp.status} ${text}`);
    }
    const data = await resp.json();
    return {
        totalPoints: Number(data?.total_points || 0),
        totalEarned: Number(data?.total_earned || 0),
        totalSpent: Number(data?.total_spent || 0),
    };
};

export const setMusicEngineAccessToken = (token) => {
    if (token) {
        localStorage.setItem('me_access_token', token);
        musicEngineAccessToken = token;
    } else {
        localStorage.removeItem('me_access_token');
        musicEngineAccessToken = null;
    }
};

// Resolve MusicEngine public root (without trailing /api)
const getMusicEngineRootUrl = () => {
    const base = (MUSIC_ENGINE_URL || '').replace(/\/$/, '');
    // If base ends with /api, drop it to reach site root
    return base.replace(/\/api$/, '');
};

// Read a cookie value by name (browser-only)
const getCookie = (name) => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
};

// Attempt MusicEngine login via Firebase verify endpoint (returns bearer token)
export const musicEngineFirebaseVerify = async (firebaseUser) => {
    if (!firebaseUser) {
        return null;
    }

    try {
        const idToken = await firebaseUser.getIdToken(true);
        const params = new URLSearchParams();
        params.append('id_token', idToken);
        if (firebaseUser.uid) params.append('user[uid]', firebaseUser.uid);
        if (firebaseUser.email) params.append('user[email]', firebaseUser.email);
        if (firebaseUser.displayName) params.append('user[displayName]', firebaseUser.displayName);
        if (firebaseUser.photoURL) params.append('user[photoURL]', firebaseUser.photoURL);

        const root = getMusicEngineRootUrl() || 'https://listen.sheqonomi.com';
        const url = `${root}/auth/firebase/verify`;

        // First try a simple POST (prod may not require CSRF)
        let resp;
        try {
            resp = await axios.post(url, params.toString(), {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
        } catch (err) {
            const status = err?.response?.status;
            // On CSRF (419) or unauthorized due to missing cookie, attempt cookie-based flow
            if (status === 419 || status === 401) {
                try {
                    // Fetch CSRF cookie (Laravel Sanctum) or fall back to root
                    try {
                        await axios.get(`${root}/sanctum/csrf-cookie`, { withCredentials: true });
                    } catch (_) {
                        await axios.get(`${root}/`, { withCredentials: true });
                    }
                    const xsrf = getCookie('XSRF-TOKEN');
                    const headers = {
                        Accept: 'application/json',
                        'Content-Type': 'application/x-www-form-urlencoded',
                    };
                    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
                    resp = await axios.post(url, params.toString(), {
                        headers,
                        withCredentials: true,
                    });
                } catch (retryErr) {
                    throw retryErr;
                }
            } else {
                throw err;
            }
        }

        const token = resp?.data?.token || resp?.data?.access_token;
        if (token) {
            setMusicEngineAccessToken(token);
            // Prefer subsequent API calls to use the same origin used for verify
            musicEngineApiBaseOverride = `${root.replace(/\/$/, '')}/api`;
            return token;
        }
        return null;
    } catch (error) {
        // Leave detailed handling to caller; return null to allow fallback flows
        return null;
    }
};

// Verify then immediately fetch current user from MusicEngine
export const fetchMusicEngineUserAfterVerify = async (firebaseUser) => {
    const token = await musicEngineFirebaseVerify(firebaseUser);
    try {
        const meUser = await musicEnginePost('/auth/user');
        return { token, meUser };
    } catch (e) {
        return { token, meUser: null };
    }
};

// Fetch current user from MusicEngine (requires Bearer token)
export const getMusicEngineUser = async () => {
    const resp = await musicEnginePost('/auth/user');
    // Fire-and-forget: kick off points balance fetch so it shows in Network
    try {
        fetchMusicEnginePointsBalanceRaw()
            .then((b) => {
                try { localStorage.setItem('me_points_total', String(b?.totalPoints ?? '')); } catch (_) {}
            })
            .catch(() => {});
    } catch (_) {}
    // Unwrap common response shapes
    const meUser = (resp && (resp.user || resp.data?.user || resp.data)) || resp;
    return meUser;
};

// Unified current-user fetcher for the app
// Returns { user: { ... } } regardless of backend
export const getUnifiedCurrentUser = async () => {
    try {
        const meToken = localStorage.getItem('me_access_token');
        if (meToken) {
            const meUser = await getMusicEngineUser();
            // Normalize to legacy shape used in pages
            const normalized = {
                _id: String(meUser.id || ''),
                uid: String(meUser.id || ''),
                email: meUser.email || '',
                displayName: meUser.name || meUser.username || '',
                name: meUser.name || meUser.username || '',
                photoURL: meUser.artwork_url || meUser.avatar_url || meUser.photo_url || '',
                followCount: Number(meUser.following_count || meUser.followingCount || 0),
                gender: (meUser.gender || meUser.profile?.gender || meUser.profile?.sex || '').toString().trim(),
                country: (meUser.country || meUser.country_code || meUser.profile?.country || meUser.profile?.country_code || meUser.profile?.location || '').toString().trim(),
                // birth may not be exposed on all installs; try several keys and convert to MM/DD/YYYY if needed
                birth: (
                    meUser.birth ||
                    meUser.birth_date ||
                    meUser.dob ||
                    meUser.date_of_birth ||
                    meUser.birthday ||
                    meUser.profile?.birth ||
                    meUser.profile?.dob ||
                    meUser.profile?.birth_date ||
                    meUser.profile?.date_of_birth ||
                    ''
                ).toString().trim(),
            };
            // Normalize birth into a comparable value
            if (normalized.birth && /^\d{4}-\d{2}-\d{2}$/.test(normalized.birth)) {
                // Convert YYYY-MM-DD -> MM/DD/YYYY
                const [y, m, d] = normalized.birth.split('-');
                normalized.birth = `${m}/${d}/${y}`;
            }
            const completedLocal = localStorage.getItem('me_profile_completed') === '1';
            const hasBasics = Boolean((normalized.name || normalized.displayName) && normalized.gender && normalized.country);
            const hasDob = Boolean(normalized.birth);
            normalized.profileCompleted = completedLocal || hasBasics || hasDob;
            return { user: normalized };
        }
    } catch (e) {
        // ignore
    }
    // Try to obtain a MusicEngine token via Firebase verify, then fetch user
    try {
        const userObj = auth.currentUser;
        if (userObj) {
            const token = await ensureMusicEngineTokenForFirebaseUser(userObj);
            if (token) {
                const meUser = await getMusicEngineUser();
                const normalized = {
                    _id: String(meUser.id || ''),
                    uid: String(meUser.id || ''),
                    email: meUser.email || '',
                    displayName: meUser.name || meUser.username || '',
                    name: meUser.name || meUser.username || '',
                    photoURL: meUser.artwork_url || '',
                    followCount: Number(meUser.following_count || meUser.followingCount || 0),
                    gender: (meUser.gender || meUser.profile?.gender || '').toString().trim(),
                    country: (meUser.country || meUser.profile?.country || '').toString().trim(),
                    birth: (meUser.birth || meUser.birth_date || meUser.dob || meUser.profile?.birth || '').toString().trim(),
                };
                if (normalized.birth && /^\d{4}-\d{2}-\d{2}$/.test(normalized.birth)) {
                    const [y, m, d] = normalized.birth.split('-');
                    normalized.birth = `${m}/${d}/${y}`;
                }
                const completedLocal = localStorage.getItem('me_profile_completed') === '1';
                const hasBasics = Boolean((normalized.name || normalized.displayName) && normalized.gender && normalized.country);
                const hasDob = Boolean(normalized.birth);
                normalized.profileCompleted = completedLocal || hasBasics || hasDob;
                return { user: normalized };
            }
        }
    } catch {}

    // No legacy fallback. Return null user when MusicEngine token is unavailable
    return { user: null };
};

// Removed legacy email/password login & signup flows in favor of Firebase verify


// Attempt to automatically provision/login a MusicEngine account for a Firebase user
// Strategy:
// - If a token already exists, reuse it
// - Try to login using email as username with a deterministic password based on uid
// - If login fails, try username derived from email local-part or uid
// - If still fails, attempt signup, then login
export const ensureMusicEngineTokenForFirebaseUser = async (firebaseUser) => {
    try {
        if (!firebaseUser) {
            setMusicEngineAccessToken(null);
            return null;
        }

        // Prevent rapid re-entrancy when backend is failing
        const lastAttempt = Number(localStorage.getItem('me_token_last_attempt') || 0);
        const now = Date.now();
        if (now - lastAttempt < 15000) { // 15s cooldown
            return localStorage.getItem('me_access_token');
        }
        localStorage.setItem('me_token_last_attempt', String(now));

        const existingToken = localStorage.getItem('me_access_token');
        if (existingToken) {
            return existingToken;
        }

        // Only use Firebase verify flow (server returns MusicEngine bearer token)
        try {
            const verifiedToken = await musicEngineFirebaseVerify(firebaseUser);
            if (verifiedToken) {
                return verifiedToken;
            }
        } catch (verifyErr) {}

        return null;
    } catch (error) {
        return null;
    }
};

// Discover page specific API calls
export const getMusicEngineHomeData = async () => {
    const response = await musicEngineGet('/homepage');
    
    // Apply podcast filtering to homepage data
    if (response && response.channels) {
        const filteredChannels = filterTrendingChannels(response.channels);
        
        // Log filtering stats
        const originalPodcastCount = response.channels.reduce((total, channel) => {
            return total + (channel.objects?.data?.length || 0);
        }, 0);
        const filteredPodcastCount = filteredChannels.reduce((total, channel) => {
            return total + (channel.objects?.data?.length || 0);
        }, 0);
        logFilteringStats(originalPodcastCount, filteredPodcastCount, 'homepage');
        
        return {
            ...response,
            channels: filteredChannels
        };
    }
    
    return response;
};

export const getMusicEngineCategories = async () => {
    return await musicEngineGet('/podcasts/categories');
};

export const getMusicEngineLanguages = async () => {
    return await musicEngineGet('/podcasts/languages');
};

export const getMusicEngineCountries = async () => {
    return await musicEngineGet('/podcasts/countries');
};

export const getMusicEngineRegions = async () => {
    return await musicEngineGet('/podcasts/regions');
};

// Trending podcasts (MusicEngine)
export const getMusicEngineTrending = async () => {
    // Prefer remote endpoint if baseURL points to local; the instance will prepend base URL
    // If the production endpoint is desired, override by calling axios directly here
    try {
        // Explicitly call /api/trending by composing full URL from MUSIC_ENGINE_URL
        const base = (RESOLVED_ME_BASE || '').replace(/\/$/, '');
        const url = `${base}/trending`; // base already ends with /api → results in /api/trending
        const resp = await axios.get(url, { headers: { Accept: 'application/json', 'X-Platform': 'kaios' } });
        
        // Apply podcast filtering (KaiOS logic)
        const originalData = resp.data;
        const filteredChannels = filterTrendingChannels(originalData.channels || []);
        const filteredData = {
            ...originalData,
            channels: filteredChannels
        };
        
        // Log filtering stats
        const originalPodcastCount = (originalData.channels || []).reduce((total, channel) => {
            return total + (channel.objects?.data?.length || 0);
        }, 0);
        const filteredPodcastCount = filteredChannels.reduce((total, channel) => {
            return total + (channel.objects?.data?.length || 0);
        }, 0);
        logFilteringStats(originalPodcastCount, filteredPodcastCount, 'trending');
        
        return filteredData;
    } catch (err) {
        // Fallback to production URL if local instance is unavailable
        try {
            const resp = await axios.get('https://listen.sheqonomi.com/api/trending', {
                headers: { Accept: 'application/json', 'X-Platform': 'kaios' },
            });
            
            // Apply podcast filtering (KaiOS logic)
            const originalData = resp.data;
            const filteredChannels = filterTrendingChannels(originalData.channels || []);
            const filteredData = {
                ...originalData,
                channels: filteredChannels
            };
            
            // Log filtering stats
            const originalPodcastCount = (originalData.channels || []).reduce((total, channel) => {
                return total + (channel.objects?.data?.length || 0);
            }, 0);
            const filteredPodcastCount = filteredChannels.reduce((total, channel) => {
                return total + (channel.objects?.data?.length || 0);
            }, 0);
            logFilteringStats(originalPodcastCount, filteredPodcastCount, 'trending-fallback');
            
            return filteredData;
        } catch (e) {
            throw err;
        }
    }
};

export const getMusicEnginePodcastsByCategory = async (categorySlug) => {
    const response = await musicEngineGet(`/podcasts/category/${categorySlug}`);
    return filterMusicEnginePodcasts(response);
};

export const getMusicEnginePodcastsByLanguage = async (languageId) => {
    const response = await musicEngineGet(`/podcasts/language/${languageId}`);
    return filterMusicEnginePodcasts(response);
};

export const getMusicEnginePodcastsByCountry = async (countryCode) => {
    const response = await musicEngineGet(`/podcasts/country/${countryCode}`);
    return filterMusicEnginePodcasts(response);
};

export const getMusicEnginePodcastsByRegion = async (regionSlug) => {
    const response = await musicEngineGet(`/podcasts/region/${regionSlug}`);
    return filterMusicEnginePodcasts(response);
};

// Podcast detail
export const getMusicEnginePodcastDetail = async (podcastId) => {
    return await musicEngineGet(`/podcast/${podcastId}`);
};

// Episode detail
export const getMusicEngineEpisodeDetail = async (episodeId) => {
    return await musicEngineGet(`/episode/${episodeId}`);
};

// Artist followers
export const getMusicEngineArtistFollowers = async (artistId) => {
    return await musicEngineGet(`/artist/${artistId}/followers`);
};

// User following list (podcasts) → return total count robustly
export const getMusicEngineUserFollowingCount = async (userId) => {
    try {
        const data = await musicEngineGet(`/user/${userId}/following`);
        if (!data) return 0;
        if (Array.isArray(data)) return data.length;
        if (Array.isArray(data.data)) return data.data.length || (data.pagination?.total ?? 0);
        if (typeof data.total === 'number') return data.total;
        if (typeof data.count === 'number') return data.count;
        return 0;
    } catch (_) {
        return 0;
    }
};

// User favorites list → count only podcasts
export const getMusicEngineUserPodcastFavoritesCount = async (userId) => {
    try {
        // Prefer dedicated podcasts favorites endpoint
        const data = await musicEngineGet(`/user/${userId}/favorites/podcasts`, { page: 1, per_page: 1 });
        const total = data?.podcasts?.pagination?.total;
        if (typeof total === 'number') return total;
        // Fallback to legacy favorites and filter podcasts
        let page = 1; const perPage = 100; let totalPages = 1; let count = 0;
        const isPodcastRow = (row) => {
            const lt = (row?.loveable_type || row?.type || row?.object_type || '').toString().toLowerCase();
            if (lt.includes('podcast')) return true;
            if (lt.includes('app\\models\\podcast') || lt.includes('app/models/podcast')) return true;
            if (row?.podcast && !row?.episode) return true;
            return false;
        };
        do {
            const fav = await musicEngineGet(`/user/${userId}/favorites`, { page, per_page: perPage });
            const rows = Array.isArray(fav) ? fav : (Array.isArray(fav?.data) ? fav.data : []);
            count += rows.filter(isPodcastRow).length;
            const p = fav?.pagination;
            totalPages = Number(p?.last_page || p?.lastPage || 1) || 1;
            page += 1;
        } while (page <= totalPages);
        return count;
    } catch (_) {
        return 0;
    }
};


// Follow / Unfollow (favorite) objects on MusicEngine
// objectType: 'podcast' | 'artist' | 'album' | 'song' | 'playlist' | 'episode'
export const musicEngineToggleFavorite = async ({ id, objectType, action }) => {
    // action: true => follow/favorite, false => unfollow/unfavorite
    return await musicEnginePost('/auth/user/favorite', {
        id,
        object_type: objectType,
        action: !!action,
    });
};



